import type { FC } from "react"
import React from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import SimpleLoader from "~/components/SimpleLoader"
import { DEVICE } from "~/constants/device-widths"
import { addCommas } from "~/containers/Proposal/Brochure/SystemSummarySlide/Sidebar"
import {
  brochureState,
  formatEnergyOffset,
  offsetAsPercentage,
} from "~/containers/Proposal/state/brochure/brochure-state"
import {
  poisonedDesignState,
  systemSizeInKwState,
} from "~/containers/Proposal/state/designs/design-state"
import { annualUsageState } from "~/containers/Proposal/state/proposal/proposal-state"
import { brochureValuesLoadingState } from "~/containers/Proposal/state/sync-state"
import { replaceCharsForCY } from "~/utils/replaceCharsForCY"
import { fontWeight } from "~/utils/styles"

import { useDesignerCapabilities } from "../designer-capabilities"
import { activePanelIndex, validPanelIndex } from "../state/elements/panels"

const Wrapper = styled.div`
  width: fit-content;
  border-radius: 4px;
  background: var(--white);
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  right: 30px;
  bottom: 70px;
  flex-direction: column;
  @media ${DEVICE.laptop} {
    height: 70px;
    right: unset;
    left: 50%;
    transform: translate(-50%, 0);
    bottom: 30px;
    flex-flow: row;
  }
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  z-index: var(--z-toolbar);
`

const Item = styled.div`
  height: 50px;
  :not(:last-child) {
    border-bottom: 1px solid var(--grey-border);
  }
  @media ${DEVICE.laptop} {
    height: 65%;
    :not(:last-child) {
      border: none;
      border-right: 1px solid var(--grey-border);
    }
  }
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 6px;
  label {
    font-size: 12px;
    line-height: 14px;
    text-transform: capitalize;
    color: var(--blue-dark-font);
    white-space: nowrap;
    padding: 0 15px;
    ${fontWeight(500)}
  }
  span {
    font-size: 14px;
    line-height: 17px;
    color: var(--blue);
    ${fontWeight(600)}
  }
  span:has(div) {
    width: 50px;
    height: 20px;
  }
  :not(:last-child) {
    border-right: 1px solid var(--grey-border);
  }
`

type SystemInfoArray = {
  value: string
  label: string
}[]

/** Annual production and energy offset from the brochure atoms or the host. */
const NOT_AVAILABLE = `—`

const useProductionSummary = (): {
  production: string
  offset: string
  productionPending: boolean
} => {
  const { systemInfo } = useDesignerCapabilities()
  const brochureValuesLoading = useRecoilValue(brochureValuesLoadingState)
  const brochureOffset = useRecoilValue(offsetAsPercentage)
  const { annualSolarProduction, id: brochureId } = useRecoilValue(brochureState)

  // The host knows when its values are final: unknown production reads as
  // unavailable, and only a save in flight reads as pending.
  if (systemInfo.kind === `provided`) {
    return {
      production:
        systemInfo.annualProductionKwh === null
          ? NOT_AVAILABLE
          : `${systemInfo.annualProductionKwh.toLocaleString(`en-US`)} kWh`,
      offset:
        systemInfo.energyOffset === null
          ? NOT_AVAILABLE
          : formatEnergyOffset(systemInfo.energyOffset),
      productionPending: systemInfo.isRecalculating,
    }
  }

  return {
    production: `${annualSolarProduction.toLocaleString(`en-US`)} kWh`,
    offset: brochureOffset,
    productionPending: brochureValuesLoading || !brochureId,
  }
}

const SystemInfoPanel_Internal: FC = () => {
  const { t } = useTranslation()

  const systemSize = useRecoilValue(systemSizeInKwState)
  const { production, offset, productionPending } = useProductionSummary()
  const poisonedDesign = useRecoilValue(poisonedDesignState)
  const usage = useRecoilValue(annualUsageState)
  const panelCount = useRecoilValue(validPanelIndex).length
  const activePanelsCount = useRecoilValue(activePanelIndex).length
  const systemInfo: Readonly<SystemInfoArray> = [
    {
      value: `${systemSize} kW`,
      label: t(`systemSize`),
    },
    {
      value: `${addCommas(Math.round(usage))} kWh`,
      label: t(`annualConsumption`),
    },
    {
      value: production,
      label: t(`annualProduction`),
    },
    {
      value: `${offset} `,
      label: t(`energyOffset`),
    },
  ]

  return (
    <Wrapper>
      <>
        <Item>
          <label>{t(`panelCount`)}</label>
          <span>
            <span
              data-testid="system-info-active-panels-count"
              style={{ color: `#77C889` }}
            >
              {activePanelsCount}
            </span>
            /{panelCount}
          </span>
        </Item>
        {systemInfo.map(({ value, label }, index) => {
          const recalculatedValues =
            value === production || value === `${offset} `

          return (
            <Item key={index}>
              <label>{label}</label>
              <span
                data-testid={`system-info-panel-${replaceCharsForCY(label)}`}
              >
                {(() => {
                  if (recalculatedValues) {
                    if (productionPending && !poisonedDesign) {
                      return <SimpleLoader />
                    }
                    if (poisonedDesign) {
                      return `N/A`
                    }
                  }

                  return value
                })()}
              </span>
            </Item>
          )
        })}
      </>
    </Wrapper>
  )
}

export const SystemInfoPanel = React.memo(SystemInfoPanel_Internal)
